/**
 * In-process MCP server that exposes the agent's Conveyor tools to the spawned
 * `claude` CLI over Streamable HTTP on loopback.
 *
 * The tool handlers must run IN the agent process — they call
 * `connection.call(...)` on the live task-token `AgentConnection`. So instead of
 * spawning a separate stdio MCP child and bridging calls back over a socket, we
 * serve the same in-process handlers over an HTTP MCP transport bound to
 * `127.0.0.1`, and point `claude` at it via `--mcp-config` (type: "http"). This
 * is the wiring the PTY harness deferred ("S4"); the SDK harness already gets
 * these tools in-process.
 *
 * Sessions: one StreamableHTTPServerTransport per MCP session, created on each
 * InitializeRequest and routed by the `Mcp-Session-Id` header. A stateful
 * transport is single-session — it rejects any second initialize with 400
 * "Server already initialized" — so reusing one instance for the whole PTY run
 * made a CLI-side reconnect (after a tool call outlived its timeout during an
 * API socket flap) permanently unrecoverable: the CLI sat in "MCP reconnecting"
 * until the pod was replaced (2026-07-08 outage). Requests for an unknown or
 * expired session id get a 404, which tells the CLI to re-initialize.
 *
 * Security: bound to loopback only (never 0.0.0.0) and gated by a random
 * per-run bearer token that `claude` echoes from the generated mcp-config.
 */

import { createServer, type Server as HttpServer } from "node:http";
import { z } from "zod";
import type { IncomingMessage, ServerResponse } from "node:http";
import { writeFile } from "node:fs/promises";
import { join } from "node:path";
import { randomBytes } from "node:crypto";
import { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import { StreamableHTTPServerTransport } from "@modelcontextprotocol/sdk/server/streamableHttp.js";
import { isInitializeRequest } from "@modelcontextprotocol/sdk/types.js";
import type { HarnessMcpServer, HarnessToolDefinition } from "../types.js";
import { isExternalMcpStdioServer } from "../types.js";
import { PtyMcpServer } from "./mcp-server.js";

const LOOPBACK = "127.0.0.1";

/**
 * Ceiling on concurrently tracked MCP sessions. Each CLI reconnect abandons
 * its predecessor without a terminating DELETE, so evict oldest-first instead
 * of growing for the lifetime of a flappy PTY session. The live CLI holds
 * exactly one session; anything beyond a couple is already garbage.
 */
const MAX_SESSIONS = 16;

interface McpSession {
  transport: StreamableHTTPServerTransport;
  mcp: McpServer;
}

export interface PtyToolServerHandle {
  /** `http://127.0.0.1:<port>/mcp` — the URL written into the mcp-config. */
  url: string;
  /** Bearer token required on every request (defense-in-depth over loopback). */
  token: string;
}

export class PtyToolServer {
  private http: HttpServer | null = null;
  private readonly sessions = new Map<string, McpSession>();
  private readonly token = randomBytes(24).toString("base64url");

  constructor(
    private readonly name: string,
    private readonly tools: HarnessToolDefinition[],
  ) {}

  async start(): Promise<PtyToolServerHandle> {
    const server = createServer((req, res) => {
      void this.handle(req, res);
    });
    await new Promise<void>((resolve, reject) => {
      server.once("error", reject);
      server.listen(0, LOOPBACK, () => resolve());
    });

    const address = server.address();
    const port = address && typeof address === "object" ? address.port : 0;

    this.http = server;
    return { url: `http://${LOOPBACK}:${port}/mcp`, token: this.token };
  }

  /** Fresh McpServer with every tool registered — one per MCP session. */
  private buildMcpServer(): McpServer {
    const mcp = new McpServer({ name: this.name, version: "1.0.0" });
    // The SDK's `registerTool()` is heavily generic over the zod shape; our tool
    // defs are intentionally loose-typed (`schema: z.ZodRawShape`, handler
    // returns MCP content). Cast the registrar to a simpler shape to bridge the
    // boundary without widening to `any` — the SDK still validates input
    // against `inputSchema`.
    const register = mcp.registerTool.bind(mcp) as unknown as (
      name: string,
      config: { description: string; inputSchema: unknown; _meta?: Record<string, unknown> },
      cb: (args: unknown) => unknown,
    ) => void;
    for (const tool of this.tools) {
      // `strict` tools reject unknown input keys with a validation error naming
      // the key, instead of Zod's default strip — which on all-optional tools
      // turns a misspelled key into a silent no-op "success".
      const inputSchema = tool.strict ? z.strictObject(tool.schema) : tool.schema;
      register(
        tool.name,
        {
          description: tool.description,
          inputSchema,
          // The spawned CLI reads `_meta['anthropic/alwaysLoad']` from the
          // `tools/list` response and preloads the tool into the prompt
          // instead of deferring it behind ToolSearch. Per-tool (vs per-server
          // `alwaysLoad` in the mcp-config) so only the hot protocol tools pay
          // the prompt cost while the long tail stays deferred.
          ...(tool.alwaysLoad ? { _meta: { "anthropic/alwaysLoad": true } } : {}),
        },
        (args) => tool.handler(args),
      );
    }
    return mcp;
  }

  private async handle(req: IncomingMessage, res: ServerResponse): Promise<void> {
    if (req.headers.authorization !== `Bearer ${this.token}`) {
      res.writeHead(401).end();
      return;
    }
    try {
      const sessionId = req.headers["mcp-session-id"];
      if (typeof sessionId === "string") {
        const session = this.sessions.get(sessionId);
        if (!session) {
          // Unknown/evicted session (e.g. the CLI reconnecting with a stale
          // id) — 404 per spec so the client re-initializes.
          jsonRpcError(res, 404, -32001, "Session not found");
          return;
        }
        await session.transport.handleRequest(req, res);
        return;
      }

      // No session header: only a POSTed InitializeRequest may open a session.
      if (req.method !== "POST") {
        jsonRpcError(res, 400, -32600, "Bad Request: Mcp-Session-Id header is required");
        return;
      }
      const body: unknown = await readJsonBody(req);
      const messages = Array.isArray(body) ? body : [body];
      if (!messages.some((m) => isInitializeRequest(m))) {
        jsonRpcError(res, 400, -32600, "Bad Request: Mcp-Session-Id header is required");
        return;
      }
      await this.openSession(req, res, body);
    } catch {
      if (res.headersSent) res.end();
      else res.writeHead(500).end();
    }
  }

  /** Connect a fresh transport + McpServer pair and serve the initialize. */
  private async openSession(
    req: IncomingMessage,
    res: ServerResponse,
    parsedBody: unknown,
  ): Promise<void> {
    const mcp = this.buildMcpServer();
    const transport = new StreamableHTTPServerTransport({
      sessionIdGenerator: () => randomBytes(16).toString("hex"),
      enableJsonResponse: true,
      onsessioninitialized: (sid) => {
        this.sessions.set(sid, { transport, mcp });
        this.evictOverflow(sid);
      },
    });
    transport.onclose = () => {
      const sid = transport.sessionId;
      if (sid && this.sessions.get(sid)?.transport === transport) {
        this.sessions.delete(sid);
      }
    };
    await mcp.connect(transport);
    await transport.handleRequest(req, res, parsedBody);
  }

  /** Drop oldest abandoned sessions past MAX_SESSIONS (never the newest). */
  private evictOverflow(currentSid: string): void {
    for (const [sid, session] of this.sessions) {
      if (this.sessions.size <= MAX_SESSIONS) break;
      if (sid === currentSid) continue;
      this.sessions.delete(sid);
      void closeSession(session);
    }
  }

  async close(): Promise<void> {
    const sessions = [...this.sessions.values()];
    this.sessions.clear();
    for (const session of sessions) {
      await closeSession(session);
    }
    const http = this.http;
    this.http = null;
    if (http) {
      await new Promise<void>((resolve) => {
        http.close(() => resolve());
      });
    }
  }
}

async function closeSession(session: McpSession): Promise<void> {
  try {
    await session.transport.close();
  } catch {
    /* already closed */
  }
  try {
    await session.mcp.close();
  } catch {
    /* already closed */
  }
}

function jsonRpcError(res: ServerResponse, status: number, code: number, message: string): void {
  res.writeHead(status, { "Content-Type": "application/json" });
  res.end(JSON.stringify({ jsonrpc: "2.0", error: { code, message }, id: null }));
}

async function readJsonBody(req: IncomingMessage): Promise<unknown> {
  const chunks: Buffer[] = [];
  for await (const chunk of req) {
    chunks.push(chunk as Buffer);
  }
  return JSON.parse(Buffer.concat(chunks).toString("utf8")) as unknown;
}

export interface ToolServersResult {
  servers: PtyToolServer[];
  /** Path to the written `--mcp-config`, or null when there were no tools. */
  mcpConfigPath: string | null;
}

type McpConfigEntry =
  | { type: "http"; url: string; headers: Record<string, string> }
  | { type: "stdio"; command: string; args?: string[]; env?: Record<string, string> };

/**
 * Start one loopback HTTP MCP server per harness tool group (in practice just
 * "conveyor"), pass external stdio entries (e.g. the baked `playwright-mcp`)
 * straight into the config, and write the `--mcp-config` the spawned `claude`
 * consumes. No-op (null config) when there is nothing to serve.
 */
export async function startToolServers(
  mcpServers: Record<string, HarnessMcpServer>,
  tempDir: string,
): Promise<ToolServersResult> {
  const servers: PtyToolServer[] = [];
  const config: Record<string, McpConfigEntry> = {};
  for (const [name, handle] of Object.entries(mcpServers)) {
    if (isExternalMcpStdioServer(handle)) {
      config[name] = {
        type: "stdio",
        command: handle.command,
        ...(handle.args ? { args: handle.args } : {}),
        ...(handle.env ? { env: handle.env } : {}),
      };
      continue;
    }
    const tools = handle instanceof PtyMcpServer ? handle.tools : [];
    if (tools.length === 0) continue;
    const server = new PtyToolServer(name, tools);
    const { url, token } = await server.start();
    servers.push(server);
    config[name] = { type: "http", url, headers: { Authorization: `Bearer ${token}` } };
  }
  if (Object.keys(config).length === 0) return { servers, mcpConfigPath: null };
  const mcpConfigPath = join(tempDir, "mcp-config.json");
  await writeFile(mcpConfigPath, JSON.stringify({ mcpServers: config }, null, 2), "utf8");
  return { servers, mcpConfigPath };
}
